/**
 * AdminJourneyDiagnostics · Feb 2026
 *
 * Live view of audio-journey render health. Built after Antonio Marbella's
 * beta tester bug where installs failed with a generic "Something went wrong"
 * and we had no way to see the real exception. Three panels:
 *
 *   HEALTH       → topline counts + 24h failure rate + top error signatures
 *   FAILED       → last N failed renders with raw traceback, searchable by email
 *   STUCK        → anything still "rendering" past the reaper's 8-min cutoff
 *
 * Admin can tap "Force retry" on any failed journey to re-kick the render.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import { VoiceMissesPanel } from "../../components/admin/VoiceMissesPanel";
import { ArrowLeft, Loader2, RefreshCw, AlertCircle, CheckCircle2, Clock, Mail, PlayCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

export default function AdminJourneyDiagnostics() {
  const nav = useNavigate();
  const [tab, setTab] = useState("health"); // health | failed | stuck
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState([]);
  const [stuck, setStuck] = useState([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const emailQ = emailFilter.trim() ? `?email=${encodeURIComponent(emailFilter.trim())}` : "";
      const [h, f, s] = await Promise.all([
        fetch(`${API}/api/admin/journey-diagnostics/health`, { headers: auth() }),
        fetch(`${API}/api/admin/journey-diagnostics/failed${emailQ}`, { headers: auth() }),
        fetch(`${API}/api/admin/journey-diagnostics/stuck`, { headers: auth() }),
      ]);
      if (!h.ok) throw new Error(`health HTTP ${h.status}`);
      setHealth(await h.json());
      setFailed((await f.json()).items || []);
      setStuck((await s.json()).items || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const forceRetry = async (jid) => {
    setRetrying(jid);
    try {
      const r = await fetch(`${API}/api/admin/journey-diagnostics/retry/${jid}`, {
        method: "POST", headers: auth(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setToast(`Retry kicked for ${jid}`);
      setTimeout(() => setToast(""), 3000);
      await load();
    } catch (e) {
      setToast(`Retry failed: ${e.message}`);
      setTimeout(() => setToast(""), 4000);
    } finally {
      setRetrying(null);
    }
  };

  if (loading && !health) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-[#4A6741]" size={32} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-journey-diagnostics">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl text-[#292524]">Journey Render Diagnostics</h1>
            <p className="text-sm text-[#78716C]">Live view of audio-journey render health. Debug failed renders, force retries.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/admin")} className="text-sm text-[#78716C] flex items-center gap-1" data-testid="back-to-admin">
              <ArrowLeft size={16} /> Admin
            </button>
            <button onClick={load} className="p-2 rounded-full hover:bg-[#F5F3F0]" data-testid="refresh">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-xl" data-testid="error">
            {err}
          </div>
        )}
        {toast && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-xl" data-testid="toast">
            {toast}
          </div>
        )}

        {/* Topline health card */}
        {health && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="health-tiles">
            <HealthTile label="Ready (total)" value={health.counts_total.ready} tone="good" />
            <HealthTile label="Failed (total)" value={health.counts_total.failed} tone={health.counts_total.failed > 0 ? "bad" : "neutral"} />
            <HealthTile label="Rendering now" value={health.counts_total.rendering} tone="neutral" />
            <HealthTile label="Stuck > 8m" value={health.stuck_rendering_over_8min} tone={health.stuck_rendering_over_8min > 0 ? "warn" : "good"} />
            <HealthTile label="24h fail rate" value={`${(health.failure_rate_24h * 100).toFixed(1)}%`} tone={health.failure_rate_24h > 0.1 ? "bad" : health.failure_rate_24h > 0.03 ? "warn" : "good"} />
          </div>
        )}

        {/* Top errors in last 24h */}
        {health?.top_errors_24h?.length > 0 && (
          <div className="bg-white border border-[#E6E2DD] rounded-xl p-4" data-testid="top-errors">
            <h2 className="text-sm font-semibold text-[#292524] mb-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-500" />
              Top error signatures · last 24h
            </h2>
            <div className="space-y-1">
              {health.top_errors_24h.map((row, idx) => (
                <div key={idx} className="flex justify-between text-sm font-mono">
                  <span className="truncate text-[#292524]">{row.error || "(no message)"}</span>
                  <span className="text-[#78716C] ml-4 flex-shrink-0">{row.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 7-day sparkline + pre-flight self-test */}
        {health?.daily_series_7d && (
          <div className="bg-white border border-[#E6E2DD] rounded-xl p-4" data-testid="sparkline-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#292524]">7-day daily output</h2>
              <SelfTestButton />
            </div>
            <Sparkline series={health.daily_series_7d} />
            <p className="text-xs text-[#78716C] mt-2">
              Each bar = one day. Green = installs completed · Red = installs failed.
              7-day totals: <strong>{health.counts_7d.ready}</strong> ready · <strong>{health.counts_7d.failed}</strong> failed.
            </p>
          </div>
        )}

        <VoiceMissesPanel />

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E6E2DD]">
          {[
            { id: "health", label: "Overview", count: null },
            { id: "failed", label: "Failed", count: failed.length, icon: AlertCircle },
            { id: "stuck", label: "Stuck", count: stuck.length, icon: Clock },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm ${tab === t.id ? "border-[#4A6741] text-[#4A6741]" : "border-transparent text-[#78716C]"}`}
              data-testid={`tab-${t.id}`}
            >
              {t.icon && <t.icon size={14} />}
              {t.label}
              {t.count !== null && <span className="text-xs bg-[#F5F3F0] px-2 py-0.5 rounded-full">{t.count}</span>}
            </button>
          ))}
        </div>

        {/* FAILED tab */}
        {tab === "failed" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-white border border-[#E6E2DD] rounded-xl p-3">
              <Mail size={16} className="text-[#78716C]" />
              <input
                type="email"
                placeholder="Filter by user email (e.g. antonio@example.com)"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="email-filter"
              />
              <button
                onClick={load}
                className="text-xs px-3 py-1.5 bg-[#4A6741] text-white rounded-full"
                data-testid="search-btn"
              >
                Search
              </button>
            </div>

            {failed.length === 0 ? (
              <div className="text-center py-12 text-[#78716C]">
                <CheckCircle2 className="mx-auto mb-2 text-green-500" size={32} />
                <p className="text-sm">No failed journeys{emailFilter ? ` for ${emailFilter}` : ""}.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {failed.map((j) => (
                  <FailedRow
                    key={j.id}
                    j={j}
                    expanded={expanded === j.id}
                    onToggle={() => setExpanded(expanded === j.id ? null : j.id)}
                    onRetry={() => forceRetry(j.id)}
                    retrying={retrying === j.id}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* STUCK tab */}
        {tab === "stuck" && (
          <div className="space-y-2">
            {stuck.length === 0 ? (
              <div className="text-center py-12 text-[#78716C]">
                <CheckCircle2 className="mx-auto mb-2 text-green-500" size={32} />
                <p className="text-sm">No stuck journeys. The periodic reaper is doing its job.</p>
              </div>
            ) : (
              stuck.map((j) => (
                <div key={j.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid={`stuck-${j.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#292524]">{j.name} · {j.user?.email}</p>
                      <p className="text-xs text-[#78716C] font-mono">{j.id} · created {j.created_at} · retry_count={j.retry_count || 0}</p>
                    </div>
                    <button
                      onClick={() => forceRetry(j.id)}
                      disabled={retrying === j.id}
                      className="text-xs px-3 py-1.5 bg-[#4A6741] text-white rounded-full disabled:opacity-50"
                      data-testid={`force-retry-${j.id}`}
                    >
                      {retrying === j.id ? "Retrying..." : "Force retry"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "health" && (
          <div className="bg-white border border-[#E6E2DD] rounded-xl p-4 text-sm text-[#292524] space-y-2">
            <p><strong>Total ready:</strong> {health?.counts_total.ready}</p>
            <p><strong>Total failed:</strong> {health?.counts_total.failed}</p>
            <p><strong>Rendering now:</strong> {health?.counts_total.rendering}</p>
            <p><strong>Created in last 24h:</strong> {(health?.counts_24h.ready || 0) + (health?.counts_24h.failed || 0) + (health?.counts_24h.rendering || 0)}</p>
            <p><strong>Created in last 7d:</strong> {(health?.counts_7d.ready || 0) + (health?.counts_7d.failed || 0)}</p>
            <p className="text-xs text-[#78716C] pt-2">Switch to the <strong>Failed</strong> tab to see individual errors — use the email filter to look up a specific user.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function HealthTile({ label, value, tone }) {
  const toneColors = {
    good: "border-green-200 bg-green-50 text-green-900",
    bad: "border-red-200 bg-red-50 text-red-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    neutral: "border-[#E6E2DD] bg-white text-[#292524]",
  };
  return (
    <div className={`rounded-xl border p-3 ${toneColors[tone]}`} data-testid={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-light mt-1">{value}</p>
    </div>
  );
}

function Sparkline({ series }) {
  // Grouped bar chart: one pair (ready/failed) per day across 7 days.
  // Pure inline SVG — no deps, no reflow, feels like a real chart.
  const maxVal = Math.max(1, ...series.flatMap((d) => [d.ready, d.failed]));
  const W = 300;
  const H = 48;
  const padY = 2;
  const barWidth = (W - 20) / (series.length * 2 + 1); // leave gap between days
  return (
    <svg viewBox={`0 0 ${W} ${H + 14}`} className="w-full" data-testid="sparkline">
      {series.map((d, i) => {
        const x = 10 + i * (barWidth * 2 + 2);
        const hReady = ((d.ready / maxVal) * (H - padY * 2)) | 0;
        const hFail = ((d.failed / maxVal) * (H - padY * 2)) | 0;
        return (
          <g key={d.date}>
            <rect x={x} y={H - hReady} width={barWidth - 1} height={Math.max(hReady, 1)} fill="#4A6741" opacity={d.ready ? 0.9 : 0.15} />
            <rect x={x + barWidth} y={H - hFail} width={barWidth - 1} height={Math.max(hFail, 1)} fill="#DC2626" opacity={d.failed ? 0.9 : 0.15} />
            <text x={x + barWidth} y={H + 10} fontSize="7" textAnchor="middle" fill="#78716C">
              {d.date.slice(5)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function SelfTestButton() {
  const [state, setState] = useState("idle"); // idle | running | ok | fail
  const [result, setResult] = useState(null);
  const run = async () => {
    setState("running");
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/journey-diagnostics/selftest`, { method: "POST", headers: auth() });
      const data = await r.json();
      setResult(data);
      setState(data.ok ? "ok" : "fail");
    } catch (e) {
      setResult({ error: String(e.message || e) });
      setState("fail");
    }
  };
  const label =
    state === "running" ? "Running..." :
    state === "ok" ? `✓ Pipeline OK · ${result?.elapsed_s}s` :
    state === "fail" ? `✗ Pipeline FAIL` : "Run pre-flight self-test";
  const color =
    state === "ok" ? "bg-green-600 hover:bg-green-700" :
    state === "fail" ? "bg-red-600 hover:bg-red-700" :
    "bg-[#4A6741] hover:bg-[#3a5331]";
  return (
    <div className="flex items-center gap-2" title={result?.error || ""}>
      <button
        onClick={run}
        disabled={state === "running"}
        className={`text-xs px-3 py-1.5 ${color} text-white rounded-full disabled:opacity-50`}
        data-testid="run-selftest"
      >
        {label}
      </button>
    </div>
  );
}

function FailedRow({ j, expanded, onToggle, onRetry, retrying }) {
  return (
    <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-testid={`failed-${j.id}`}>
      <button onClick={onToggle} className="w-full text-left p-3 hover:bg-red-50">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#292524] truncate">
              {j.name}
              <span className="text-xs text-[#78716C] font-normal ml-2">· {j.user?.email}</span>
            </p>
            <p className="text-xs text-red-600 mt-0.5 font-mono truncate">
              {j.audio_error_type && <span className="bg-red-100 px-1.5 py-0.5 rounded mr-2">{j.audio_error_type}</span>}
              {j.audio_error}
            </p>
            <p className="text-xs text-[#78716C] mt-1">
              {j.duration_minutes}m · {j.music_ids?.join(", ")} · retry_count={j.retry_count || 0} · failed_at={j.audio_failed_at || j.updated_at}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); onRetry(); }}
              disabled={retrying}
              className="text-xs px-3 py-1.5 bg-[#4A6741] text-white rounded-full disabled:opacity-50 flex items-center gap-1"
              data-testid={`retry-${j.id}`}
            >
              <PlayCircle size={12} />
              {retrying ? "..." : "Force retry"}
            </button>
          </div>
        </div>
      </button>
      {expanded && j.audio_error_traceback && (
        <div className="bg-[#1A1A1A] text-red-200 p-3 text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto">
          {j.audio_error_traceback}
        </div>
      )}
    </div>
  );
}
